import React, { useState } from "react";
import { saveAs } from "file-saver";
import "./quiz-form.scss";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
} from "docx";

const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0].replace(/-/g, "");
};

const defaultQuestion = {
  id: null,
  type: "single-statement",
  question: "",
  statements: [],
  options: [],
  answer: "",
  explanation: "",
  listALabel: "List A",
  listBLabel: "List B",
  listA: [],
  listB: [],
  assertion: "",
  reason: "",
};

const QuizForm = () => {
  const [questionData, setQuestionData] = useState({ ...defaultQuestion });
  const [questions, setQuestions] = useState([]);
  const [type, setType] = useState("single-statement");

  const handleChange = (field, value) => {
    setQuestionData({ ...questionData, [field]: value });
  };

  const handleArrayChange = (field, index, value) => {
    const arr = [...(questionData[field] || [])];
    arr[index] = value;
    setQuestionData({ ...questionData, [field]: arr });
  };

  const addField = (field) => {
    const arr = [...(questionData[field] || [])];
    arr.push("");
    setQuestionData({ ...questionData, [field]: arr });
  };

  const addQuestion = () => {
    let newQuestion = {
      id: questions.length + 1,
      type,
      question: questionData.question,
      statements: questionData.statements,
      options: questionData.options,
      answer: questionData.answer,
      explanation: questionData.explanation,
      assertion: questionData.assertion,
      reason: questionData.reason,
    };

    // For match-the-following, dynamically assign custom label keys
    if (type === "match-the-following") {
      if (questionData.listA.length > 0 && questionData.listALabel.trim()) {
        newQuestion[questionData.listALabel.trim()] = questionData.listA;
      }
      if (questionData.listB.length > 0 && questionData.listBLabel.trim()) {
        newQuestion[questionData.listBLabel.trim()] = questionData.listB;
      }
    }

    // Remove keys with "", null, or empty arrays
    newQuestion = Object.fromEntries(
      Object.entries(newQuestion).filter(
        ([_, value]) =>
          value !== "" &&
          value !== null &&
          !(Array.isArray(value) && value.length === 0)
      )
    );

    setQuestions([...questions, newQuestion]);
    setQuestionData({ ...defaultQuestion });
    alert(`Question added`);
  };

  const downloadJSON = () => {
    const data = {
      questions: questions,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });

    saveAs(blob, `${getTodayDate()}.json`);
  };

  // Utility to clean up HTML tags
  function stripHTML(html) {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  }

  const generateWordDoc = async () => {
    try {
      const response = await fetch("/quizzes/index.json");
      const quizFiles = await response.json();

      let paragraphs = [];
      let questionCount = 1;

      for (const quizFile of quizFiles) {
        const quizDataResponse = await fetch(`/quizzes/${quizFile}.json`);
        const quizData = await quizDataResponse.json();

        if (!quizData.questions || !Array.isArray(quizData.questions)) continue;

        for (const question of quizData.questions) {
          console.log(quizData.questions, question);
          const questionNumber = `${questionCount}.`;
          const cleanText = stripHTML(question.question || "");
          console.log(cleanText);
          // Main Question
          if (cleanText) {
            paragraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${questionNumber} ${cleanText}`,
                    bold: true,
                  }),
                ],
              })
            );
          }

          // Multi-statements
          if (question.statements?.length) {
            question.statements.forEach((stmt, idx) => {
              paragraphs.push(
                new Paragraph({
                  children: [new TextRun(`${stripHTML(stmt)}`)],
                })
              );
            });
          }

          // Assertion & Reason
          if (question.assertion && question.reason) {
            paragraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Assertion (A): ${stripHTML(question.assertion)}`,
                    bold: true,
                  }),
                ],
              })
            );
            paragraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Reason (R): ${stripHTML(question.reason)}`,
                    bold: true,
                  }),
                ],
              })
            );
          }

          // Match the following
          if (
            question.Organization &&
            question.Organization.length > 0 &&
            question.Function &&
            question.Function.length > 0
          ) {
            // Table for match-the-following with invisible borders and centered content
            const table = new Table({
              borders: {
                top: { style: "none" },
                bottom: { style: "none" },
                left: { style: "none" },
                right: { style: "none" },
                insideHorizontal: { style: "none" },
                insideVertical: { style: "none" },
              },
              rows: [
                // Header Row with centered and bold content
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Organization",
                              bold: true, // Bold TextRun
                            }),
                          ],
                          alignment: "center", // Center header text
                        }),
                      ],
                      width: { size: 50, type: "pct" }, // Adjust percentage width
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Function",
                              bold: true, // Bold TextRun
                            }),
                          ],
                          alignment: "center", // Center header text
                        }),
                      ],
                      width: { size: 50, type: "pct" }, // Adjust percentage width
                    }),
                  ],
                }),
                // Mapping of Organization and Function
                ...question.Organization.map(
                  (org, idx) =>
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: org,
                                }),
                              ],
                              // Center content
                            }),
                          ],
                          // Center content in cell
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: question.Function[idx],
                                }),
                              ],
                            }),
                          ],
                        }),
                      ],
                    })
                ),
              ],
            });

            paragraphs.push(table);
          }

          // Options (fixing bullet format like a/b/c/d)
          if (question.options?.length) {
            question.options.forEach((opt, index) => {
              const optText = stripHTML(opt);
              paragraphs.push(
                new Paragraph({
                  children: [new TextRun(` ${optText}`)],
                })
              );
            });
          }

          // Answer
          if (question.answer) {
            paragraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Answer: ${stripHTML(question.answer)}`,
                    bold: true,
                  }),
                ],
              })
            );
          }

          // Explanation
          if (question.explanation) {
            paragraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Explanation:",
                    bold: true,
                  }),
                  new TextRun({
                    text: stripHTML(question.explanation), // your actual explanation
                    break: 1, // adds a line break after "Explanation:"
                  }),
                ],
              })
            );
          }

          // Space between questions
          paragraphs.push(new Paragraph({ text: "" }));
          questionCount++;
        }
      }

      const doc = new Document({
        creator: "IAS Jnani",
        title: "UPSC Quiz Questions",
        description: "Formatted UPSC questions",
        sections: [
          {
            properties: {},
            children: paragraphs,
          },
        ],
        styles: {
          default: {
            document: {
              run: {
                font: "Calibri",
                size: 24, // 12pt = 24 half-points
              },
              paragraph: {
                spacing: {
                  after: 120, // Optional: spacing after paragraphs
                },
              },
            },
          },
        },
      });

      const blob = await Packer.toBlob(doc);
      const fileName = "Jnani_Monthly_Quiz_Magazine.docx";
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    } catch (error) {
      console.error("Error generating Word document:", error);
    }
  };

  return (
    <div className="quiz-form container py-4">
      <h2 className="mb-4">📝 UPSC Quiz Question Creator</h2>

      <div className="mb-3">
        <label className="form-label">Question Type</label>
        <select
          className="form-select"
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setQuestionData({ ...defaultQuestion });
          }}
        >
          <option value="single-statement">Single Statement</option>
          <option value="multi-statement">Multi-Statement</option>
          <option value="match-the-following">Match the Following</option>
          <option value="assertion-reason">Assertion & Reason</option>
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label">Question</label>
        <textarea
          className="form-control"
          value={questionData.question}
          onChange={(e) => handleChange("question", e.target.value)}
        />
      </div>

      {type === "multi-statement" && (
        <>
          <div className="mb-3">
            <label className="form-label">Statements</label>
          </div>
          {questionData.statements.map((stmt, idx) => (
            <input
              key={idx}
              className="form-control mb-2"
              placeholder={`Statement ${idx + 1}`}
              value={stmt}
              onChange={(e) =>
                handleArrayChange("statements", idx, e.target.value)
              }
            />
          ))}
          <button
            className="btn btn-outline-primary mb-3"
            onClick={() => addField("statements")}
          >
            ➕ Add Statement
          </button>
        </>
      )}

      {type === "match-the-following" && (
        <>
          <div className="row mb-3">
            <div className="col">
              <label>Label for List A</label>
              <input
                className="form-control"
                value={questionData.listALabel}
                onChange={(e) => handleChange("listALabel", e.target.value)}
              />
            </div>
            <div className="col">
              <label>Label for List B</label>
              <input
                className="form-control"
                value={questionData.listBLabel}
                onChange={(e) => handleChange("listBLabel", e.target.value)}
              />
            </div>
          </div>

          <div className="row">
            <div className="col">
              <h6>{questionData.listALabel}</h6>
              {questionData.listA.map((item, idx) => (
                <input
                  key={idx}
                  className="form-control mb-2"
                  placeholder={`${questionData.listALabel} ${idx + 1}`}
                  value={item}
                  onChange={(e) =>
                    handleArrayChange("listA", idx, e.target.value)
                  }
                />
              ))}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => addField("listA")}
              >
                ➕ Add {questionData.listALabel}
              </button>
            </div>
            <div className="col">
              <h6>{questionData.listBLabel}</h6>
              {questionData.listB.map((item, idx) => (
                <input
                  key={idx}
                  className="form-control mb-2"
                  placeholder={`${questionData.listBLabel} ${idx + 1}`}
                  value={item}
                  onChange={(e) =>
                    handleArrayChange("listB", idx, e.target.value)
                  }
                />
              ))}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => addField("listB")}
              >
                ➕ Add {questionData.listBLabel}
              </button>
            </div>
          </div>
        </>
      )}

      {type === "assertion-reason" && (
        <>
          <div className="mb-3">
            <label>Assertion</label>
            <input
              className="form-control"
              value={questionData.assertion}
              onChange={(e) => handleChange("assertion", e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label>Reason</label>
            <input
              className="form-control"
              value={questionData.reason}
              onChange={(e) => handleChange("reason", e.target.value)}
            />
          </div>
        </>
      )}
      <div className="mb-3">
        <label className="form-label">Options</label>
      </div>
      {questionData.options.map((opt, idx) => (
        <input
          key={idx}
          className="form-control mb-2"
          placeholder={`Option ${String.fromCharCode(65 + idx)}`}
          value={opt}
          onChange={(e) => handleArrayChange("options", idx, e.target.value)}
        />
      ))}
      <button
        className="btn btn-outline-primary mb-3 mt-3"
        onClick={() => addField("options")}
      >
        ➕ Add Option
      </button>

      <div className="mb-3">
        <label className="form-label">Answer</label>
        <input
          className="form-control"
          value={questionData.answer}
          onChange={(e) => handleChange("answer", e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Explanation</label>
        <textarea
          className="form-control"
          value={questionData.explanation}
          onChange={(e) => handleChange("explanation", e.target.value)}
        />
      </div>

      <div className="d-flex gap-3">
        <button className="btn btn-success" onClick={addQuestion}>
          ✅ Add Question
        </button>
        <button className="btn btn-primary" onClick={downloadJSON}>
          💾 Download {getTodayDate()}.json
        </button>
        <button className="btn btn-primary" onClick={generateWordDoc}>
          📥 Download All Questions as Word Doc
        </button>
      </div>

      {questions.length > 0 && (
        <p className="mt-3 text-success">
          ✔️ {questions.length} question(s) added.
        </p>
      )}
    </div>
  );
};

export default QuizForm;
